/**
 * O bloco "Dados da solicitação" do documento: quem pediu e, quando o host tem o
 * contexto da solicitação, o rateio, o motivo e a observação declarados no pedido.
 */
import { supplierConfirmation, type ApprovalRequestContext } from '@voll-intelligence/domain';
import type { VoucherDocumentField, VoucherDocumentView } from './views/voucher_document_view.js';
import type { DocumentContext } from './context.js';
import { compact, field } from './document_fields.js';
import type { Voucher } from './services.js';

/** O valor de cada centro de custo da alocação, na ordem dela (`undefined` sem nome); o
 *  percentual só entra no rateio — "100%" num centro único não diz nada ao aprovador. */
export function costCenterValues(
  costCenters: ApprovalRequestContext['costCenters'],
  ctx: DocumentContext,
): (string | undefined)[] {
  const split = costCenters.length > 1;
  const percent = new Intl.NumberFormat(ctx.locale, { maximumFractionDigits: 2 });
  return costCenters.map((allocation) => {
    const name = allocation.costCenter?.trim();
    return name && split && allocation.percent != null
      ? ctx.t('trips.voucher.costCenterSplit', {
          name,
          percent: percent.format(allocation.percent),
        })
      : name;
  });
}

/** Um campo por centro de custo da alocação. */
function costCenterFields(
  costCenters: ApprovalRequestContext['costCenters'],
  ctx: DocumentContext,
): (VoucherDocumentField | null)[] {
  return costCenterValues(costCenters, ctx).map((value, i) =>
    field(`costCenter-${String(i)}`, ctx.t('trips.voucher.costCenter'), value, { full: true }),
  );
}

/** Empresa e projeto do rateio, um valor por nome distinto — o rateio entre dois
 *  centros da mesma empresa não repete "VOLL, VOLL". */
export function allocationValue(
  costCenters: ApprovalRequestContext['costCenters'],
  pick: (allocation: ApprovalRequestContext['costCenters'][number]) => string | null,
): string {
  const names = costCenters.map((allocation) => pick(allocation)?.trim()).filter(Boolean);
  return [...new Set(names)].join(', ');
}

/** O bloco gerencial: quem PEDIU (do voucher) e, quando o host tem o contexto da
 *  solicitação (a fila de aprovações o costura da triagem), o rateio, o motivo e a
 *  observação declarados no pedido, na ordem do sistema antigo. Sem contexto, o
 *  bloco segue só com o voucher. */
export function requestInfo(
  voucher: Voucher,
  ctx: DocumentContext,
  request: ApprovalRequestContext | undefined,
): VoucherDocumentView['more'] {
  const costCenters = request?.costCenters ?? [];
  const fields = compact([
    field('requester', ctx.t('trips.voucher.requester'), voucher.requester?.name, { full: true }),
    field(
      'company',
      ctx.t('trips.voucher.company'),
      allocationValue(costCenters, (a) => a.company),
      { full: true },
    ),
    ...costCenterFields(costCenters, ctx),
    field(
      'project',
      ctx.t('trips.voucher.project'),
      allocationValue(costCenters, (a) => a.project),
      { full: true },
    ),
    field('motive', ctx.t('trips.voucher.motive'), request?.motive, { full: true }),
    field('obs', ctx.t('trips.voucher.obs'), request?.obs, { full: true }),
    field('reservation', ctx.t('trips.services.reservation'), supplierConfirmation(voucher), {
      copy: ctx.t('trips.voucher.copyReservation'),
      full: true,
    }),
  ]);
  if (fields.length === 0) return [];
  return [
    {
      key: 'request',
      title: ctx.t('trips.voucher.requestData'),
      blocks: [{ key: 'request', fields }],
    },
  ];
}
